import type { DiskUploadRecord } from '../Contracts/DiskUpload'

/**
 * Чиста логіка менеджера завантажень: розбиття на шматки, відсутні номери,
 * звірка файлу для продовження, назви вставлених зображень, швидкість.
 * Без звернень до мережі й браузерних API — під юніт-тести.
 */

/** Скільки шматків у файлі; порожній файл — 0 (як `DiskUploadPolicy::chunkCount`). */
export const chunkCount = (size: number, chunkSize: number): number =>
    size <= 0 || chunkSize <= 0 ? 0 : Math.ceil(size / chunkSize)

/**
 * Межі шматка `number` (з 0): усі по `chunkSize`, останній — залишок.
 * `null` — такого номера у файлі немає.
 */
export const chunkRange = (number: number, size: number, chunkSize: number): { start: number; end: number } | null => {
    const count = chunkCount(size, chunkSize)

    if (!Number.isInteger(number) || number < 0 || number >= count) return null

    const start = number * chunkSize

    return { start, end: Math.min(start + chunkSize, size) }
}

/** Довжина шматка `number`; неіснуючий — 0. */
export const chunkLength = (number: number, size: number, chunkSize: number): number => {
    const range = chunkRange(number, size, chunkSize)

    return range === null ? 0 : range.end - range.start
}

/**
 * Які шматки ще треба відправити: усі номери файлу, яких немає серед
 * отриманих беком. Чужі номери (поза файлом) і повтори відкидаються —
 * відповідь бека тут не довіряємо сліпо.
 */
export const missingChunks = (size: number, chunkSize: number, received: readonly number[]): number[] => {
    const count = chunkCount(size, chunkSize)
    const got = new Set(received.filter((number) => Number.isInteger(number) && number >= 0 && number < count))
    const missing: number[] = []

    for (let number = 0; number < count; number += 1) {
        if (!got.has(number)) missing.push(number)
    }

    return missing
}

/** Скільки байтів уже на сервері — сума довжин отриманих шматків. */
export const receivedBytes = (size: number, chunkSize: number, received: readonly number[]): number =>
    [...new Set(received)].reduce((sum, number) => sum + chunkLength(number, size, chunkSize), 0)

/**
 * Чи той самий файл обрав користувач для продовження сесії після
 * перезавантаження. Вміст не порівнюємо (браузер його ще не прочитав) —
 * назва, розмір і час зміни; повертає пояснення розбіжності або `null`.
 */
export const resumeMismatch = (
    record: Pick<DiskUploadRecord, 'name' | 'size' | 'lastModified'>,
    file: { name: string; size: number; lastModified: number }
): string | null => {
    if (file.name !== record.name) return `Обрано «${file.name}», а продовжити треба «${record.name}»`
    if (file.size !== record.size) return `«${file.name}» має інший розмір — це вже не той файл`
    if (file.lastModified !== record.lastModified) return `«${file.name}» змінено після початку завантаження`

    return null
}

/** Назви, які браузери дають зображенню з буфера: «image.png», порожня. */
const GENERIC_PASTED = /^(image|blob|untitled|clipboard)?\.?(png|jpe?g|gif|webp|bmp)?$/i

/** Чи в зображення з буфера немає своєї назви (скриншот, копія з браузера). */
export const isUnnamedPastedImage = (file: { name: string; type: string }): boolean =>
    file.type.startsWith('image/') && GENERIC_PASTED.test(file.name.trim())

const EXTENSIONS: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
}

const two = (value: number): string => String(value).padStart(2, '0')

/**
 * «Вставлене зображення 27.10.2026 14.05.33.png». Двокрапки в часі немає:
 * бек її вирізає з назви (сумісність із Windows і WebDAV).
 */
export const pastedImageName = (date: Date, mime: string): string => {
    const day = `${two(date.getDate())}.${two(date.getMonth() + 1)}.${date.getFullYear()}`
    const time = `${two(date.getHours())}.${two(date.getMinutes())}.${two(date.getSeconds())}`

    return `Вставлене зображення ${day} ${time}.${EXTENSIONS[mime] ?? 'png'}`
}

/** Папки з `webkitRelativePath` («Звіти/2026/файл.pdf» → «Звіти/2026»). */
export const relativeDir = (path: string): string => {
    const parts = path.split('/').filter((part) => part !== '')

    return parts.slice(0, -1).join('/')
}

/** Ключ назви для пошуку конфлікту: регістр і форма Unicode не важать (як `NodeNameKeys` беку). */
export const nameKey = (name: string): string => name.normalize('NFC').toLocaleLowerCase('uk')

export interface RateSample {
    /** Мілісекунди. */
    at: number
    bytes: number
}

/** Вікно, за яке рахуємо швидкість: досить, щоб згладити шматки по 8 МіБ. */
export const RATE_WINDOW_MS = 10_000

/** Швидкість, байт/с, за вибірками у вікні; менше двох точок — 0. */
export const transferRate = (samples: readonly RateSample[]): number => {
    const first = samples[0]
    const last = samples[samples.length - 1]

    if (first === undefined || last === undefined || last.at <= first.at) return 0

    return Math.max(0, ((last.bytes - first.bytes) * 1000) / (last.at - first.at))
}

/** Вибірки, що лишаються у вікні після нової точки (остання поза вікном — як опора). */
export const pushSample = (samples: readonly RateSample[], sample: RateSample): RateSample[] => {
    const next = [...samples, sample]
    const from = next.findIndex((item) => sample.at - item.at <= RATE_WINDOW_MS)

    return next.slice(Math.max(0, from - 1))
}

/** Залишок часу для людини: «12 с», «4 хв», «1 год 5 хв». */
export const formatEta = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds <= 0) return ''
    if (seconds < 60) return `${Math.max(1, Math.round(seconds))} с`

    const minutes = Math.round(seconds / 60)

    if (minutes < 60) return `${minutes} хв`

    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60

    return rest === 0 ? `${hours} год` : `${hours} год ${rest} хв`
}
